import {
  IPeopleProfile,
  IPeopleProfileCategory,
  PeopleFilterType,
} from '@app/core/shared/interfaces/solutions-profile.interface';
import type { IPersonPersonalInformation } from '@app/core/shared/interfaces/person-detail.interface';

/** Strip bidi / invisible unicode often present in API names. */
export function cleanPeopleProfileName(value?: string | null): string {
  if (!value) return '';
  return value.replace(/[\u200E\u200F\u202A-\u202E\u2066-\u2069]/g, '').trim();
}

export function normalizePeopleFilterType(
  raw: string | null | undefined
): PeopleFilterType | undefined {
  const upper = String(raw ?? '').toUpperCase();
  if (
    upper === 'INSTALLER' ||
    upper === 'PROJECT_OWNER' ||
    upper === 'ARCHITECT' ||
    upper === 'SUPPLIER'
  ) {
    return upper as PeopleFilterType;
  }
  return undefined;
}

/** Map list/detail API fields onto `IPeopleProfile` used by cards and detail UI. */
export function normalizePeopleProfile(
  raw: Partial<IPeopleProfile> | null | undefined
): IPeopleProfile | null {
  if (!raw) return null;

  const categories =
    raw.categories ??
    (raw.business_categories as IPeopleProfileCategory[] | undefined);

  const age =
    raw.age ??
    (typeof raw.experience === 'number' ? raw.experience : undefined);

  const type = normalizePeopleFilterType(raw.type as string | undefined);

  const first_name = cleanPeopleProfileName(raw.first_name);
  const last_name = cleanPeopleProfileName(raw.last_name);

  const exact_role =
    raw.exact_role ??
    (Array.isArray(raw.trades) && raw.trades.length > 0
      ? raw.trades.join(', ').replace(/_/g, ' ')
      : undefined);

  return {
    ...raw,
    type,
    first_name: first_name || undefined,
    last_name: last_name || undefined,
    age,
    categories,
    exact_role,
    operating_city_region: raw.operating_city_region ?? undefined,
  };
}

export function mergePersonalIntoPeopleProfile(
  job: IPeopleProfile | null | undefined,
  personal: IPersonPersonalInformation | null | undefined
): IPeopleProfile | null {
  if (!job && !personal) return null;

  return normalizePeopleProfile({
    ...(personal ?? {}),
    ...(job ?? {}),
    _id: personal?._id ?? job?._id,
    type: personal?.type ?? job?.type,
    first_name: personal?.first_name ?? job?.first_name,
    last_name: personal?.last_name ?? job?.last_name,
    title: job?.title,
    address: job?.address ?? personal?.address,
    phone: job?.contact_phone ?? job?.phone ?? personal?.mobile,
    contact_phone: job?.contact_phone ?? personal?.mobile,
    contact_email: job?.contact_email ?? personal?.email,
    is_verified: personal?.is_verified ?? job?.is_verified,
    trades: job?.trades,
    business_categories: job?.business_categories,
    scope_of_products: job?.scope_of_products,
    bio: job?.bio,
    website: job?.website,
    responsible_person: job?.responsible_person,
    operating_city_region: job?.operating_city_region,
    company_certifications: job?.company_certifications,
    insurance_documents: job?.insurance_documents,
    categories: job?.categories ?? job?.business_categories,
    age:
      job?.age ??
      (typeof job?.experience === 'number' ? job.experience : undefined),
    experience: job?.experience ?? job?.age,
  });
}
